/** 跨节点 GPU 用量校验：相同 resourceType + vendor + device 的副本数 × GPU 数量之和不超过可用上限（创建：quantity.max；编辑：详情已占用 + quantity.max） */
import type { TFunction } from 'i18next';
import { get } from 'lodash';

import type {
  MaasGroupSpec,
  MaasGroupedWorkloadSpec,
  MaasRunMode,
  MaasSharedFormValues,
  MaasSubmitError,
} from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { MaasRunMode as RunMode } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';

import { parseNodesField } from './nodeFields';
import { getMaasResourceConfigurations } from './resourceConfigurations';

export const MAAS_GPU_USAGE_EXCEEDS_MAX_KEY = 'data-center:maas.gpu.usage.exceeds.max';

export interface GpuResourceConfigurationItem {
  resourceType?: string;
  vendor?: string;
  device?: string;
  quantity?: {
    max?: number;
  };
}

export interface GpuUsageViolation {
  resourceType: string;
  vendor: string;
  device: string;
  totalUsage: number;
  maxQuantity: number;
}

export interface GpuValidationOptions {
  /** 编辑态：详情接口回填的节点，用于将已占用 GPU 计入可用上限 */
  baselineNodes?: MaasGroupSpec[];
}

let cachedGpuResourceConfigurations: GpuResourceConfigurationItem[] | undefined;
let cachedGpuResourceConfigurationsPromise: Promise<GpuResourceConfigurationItem[]> | undefined;

function requestContainerGpuResourceConfigurations(): Promise<GpuResourceConfigurationItem[]> {
  return getMaasResourceConfigurations({ workloadType: 'ContainerInstance', resourceType: 'GPU' })
    .then((res) => (Array.isArray(res) ? res : []))
    .catch((error) => {
      console.log(error);
      return [];
    });
}

/** 实时拉取容器实例 GPU 资源配置（resource-configurations），获取最新 quantity.max */
export function fetchContainerGpuResourceConfigurations(): Promise<GpuResourceConfigurationItem[]> {
  return requestContainerGpuResourceConfigurations();
}

/** 获取页面级缓存的 GPU 资源配置；首次调用时拉取并缓存，后续复用（用于节点弹窗、表单中间校验） */
export function getCachedContainerGpuResourceConfigurations(): Promise<GpuResourceConfigurationItem[]> {
  if (cachedGpuResourceConfigurations) {
    return Promise.resolve(cachedGpuResourceConfigurations);
  }
  if (!cachedGpuResourceConfigurationsPromise) {
    cachedGpuResourceConfigurationsPromise = requestContainerGpuResourceConfigurations()
      .then((configs) => {
        cachedGpuResourceConfigurations = configs;
        return configs;
      })
      .finally(() => {
        cachedGpuResourceConfigurationsPromise = undefined;
      });
  }
  return cachedGpuResourceConfigurationsPromise;
}

/** 离开创建/编辑页时清除缓存，避免跨页面复用过期数据 */
export function clearContainerGpuResourceConfigurationsCache(): void {
  cachedGpuResourceConfigurations = undefined;
  cachedGpuResourceConfigurationsPromise = undefined;
}

function buildGpuConfigKey(resourceType: string, vendor: string, device: string): string {
  return `${resourceType}\0${vendor}\0${device}`;
}

function resolveGroupGpuResourceType(gpu?: Record<string, unknown>): string {
  if (!gpu) return '';
  return String(gpu.resourceType ?? '').trim();
}

function resolveGroupReplicas(group?: MaasGroupSpec): number {
  const replicas = Number(group?.replicas);
  return Number.isFinite(replicas) && replicas > 0 ? replicas : 1;
}

function resolveGroupGpuCount(group?: MaasGroupSpec): number {
  const gpu = get(group, 'resource.gpu') as Record<string, unknown> | undefined;
  const resourceType = resolveGroupGpuResourceType(gpu);
  if (!resourceType || !gpu?.vendor || !gpu?.device) return 0;
  const count = Number(get(gpu, 'number') ?? get(gpu, 'card.request') ?? 0);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

/** 汇总各 GPU 配置项（resourceType + vendor + device）的副本数 × GPU 数量 */
export function aggregateGpuUsageByConfig(nodes: MaasGroupSpec[]): Map<string, GpuUsageViolation> {
  const usageMap = new Map<string, GpuUsageViolation>();

  nodes.forEach((group) => {
    const gpu = get(group, 'resource.gpu') as Record<string, unknown> | undefined;
    const resourceType = resolveGroupGpuResourceType(gpu);
    const vendor = String(gpu?.vendor ?? '').trim();
    const device = String(gpu?.device ?? '').trim();
    const gpuCount = resolveGroupGpuCount(group);
    if (!resourceType || !vendor || !device || gpuCount <= 0) return;

    const usage = gpuCount * resolveGroupReplicas(group);
    const key = buildGpuConfigKey(resourceType, vendor, device);
    const existing = usageMap.get(key);
    if (existing) {
      existing.totalUsage += usage;
      return;
    }
    usageMap.set(key, { resourceType, vendor, device, totalUsage: usage, maxQuantity: 0 });
  });

  return usageMap;
}

function buildBaselineUsageMap(baselineNodes?: MaasGroupSpec[]): Map<string, number> {
  if (!baselineNodes?.length) return new Map();
  const usageMap = aggregateGpuUsageByConfig(baselineNodes);
  const result = new Map<string, number>();
  usageMap.forEach((usage, key) => {
    result.set(key, usage.totalUsage);
  });
  return result;
}

function resolveGpuMaxQuantity(
  configs: GpuResourceConfigurationItem[],
  resourceType: string,
  vendor: string,
  device: string,
  baselineUsageMap?: Map<string, number>,
): number | undefined {
  const item = configs.find((config) => config.resourceType === resourceType && config.vendor === vendor && config.device === device);
  const max = item?.quantity?.max;
  if (typeof max !== 'number' || !Number.isFinite(max) || max <= 0) return undefined;
  const key = buildGpuConfigKey(resourceType, vendor, device);
  const baselineUsage = baselineUsageMap?.get(key) ?? 0;
  return max + baselineUsage;
}

/** 编辑态：从详情回填的 initValues 汇总各 GPU 配置项已占用数量 */
export function collectBaselineNodesFromInitValues(initValues?: MaasSharedFormValues): MaasGroupSpec[] | undefined {
  if (!initValues?.runMode && !initValues?.gatewayNodes && !initValues?.gateway) return undefined;
  const inferenceNodes = initValues?.runMode
    ? collectMaasNodesForGpuValidation(parseNodesField(initValues.masterNodes), parseNodesField(initValues.workerNodes), initValues.runMode)
    : [];
  const gatewayNodes = parseNodesField(initValues.gatewayNodes);
  const gateway = initValues.gateway && typeof initValues.gateway === 'object' ? [initValues.gateway] : [];
  const nodes = [...inferenceNodes, ...gatewayNodes, ...gateway];
  return nodes.length ? nodes : undefined;
}

/** 查找首个超出可用上限的 GPU 配置项（编辑态上限 = 详情已占用 + quantity.max） */
export function findGpuUsageViolation(
  nodes: MaasGroupSpec[],
  configs: GpuResourceConfigurationItem[] | undefined,
  options?: GpuValidationOptions,
): GpuUsageViolation | undefined {
  if (!configs?.length || !nodes.length) return undefined;

  const baselineUsageMap = buildBaselineUsageMap(options?.baselineNodes);
  const usageMap = aggregateGpuUsageByConfig(nodes);
  for (const usage of usageMap.values()) {
    const maxQuantity = resolveGpuMaxQuantity(configs, usage.resourceType, usage.vendor, usage.device, baselineUsageMap);
    if (maxQuantity !== undefined && usage.totalUsage > maxQuantity) {
      return { ...usage, maxQuantity };
    }
  }
  return undefined;
}

export function formatGpuUsageValidationError(t: TFunction, violation: GpuUsageViolation): string {
  return t(MAAS_GPU_USAGE_EXCEEDS_MAX_KEY, {
    resourceType: violation.resourceType,
    vendor: violation.vendor,
    device: violation.device,
    total: violation.totalUsage,
    max: violation.maxQuantity,
  });
}

export function collectMaasNodesForGpuValidation(
  masterNodes: MaasGroupSpec[],
  workerNodes: MaasGroupSpec[],
  runMode?: MaasRunMode,
): MaasGroupSpec[] {
  if (runMode === RunMode.PrimarySecondary) {
    return [...masterNodes, ...workerNodes];
  }
  return workerNodes;
}

export function findGpuUsageViolationFromNodeFields(
  masterValue: unknown,
  workerValue: unknown,
  runMode: MaasRunMode | undefined,
  configs: GpuResourceConfigurationItem[] | undefined,
  options?: GpuValidationOptions,
): GpuUsageViolation | undefined {
  const nodes = collectMaasNodesForGpuValidation(parseNodesField(masterValue), parseNodesField(workerValue), runMode);
  return findGpuUsageViolation(nodes, configs, options);
}

export function findGpuUsageViolationFromGroupedSpec(
  spec: Pick<MaasGroupedWorkloadSpec, 'headGroupSpec' | 'workerGroupSpecs' | 'gateway'>,
  configs: GpuResourceConfigurationItem[] | undefined,
  options?: GpuValidationOptions,
): GpuUsageViolation | undefined {
  const nodes = [
    ...(spec.headGroupSpec ? [spec.headGroupSpec] : []),
    ...(spec.workerGroupSpecs || []),
    ...(spec.gateway ? [spec.gateway] : []),
  ];
  return findGpuUsageViolation(nodes, configs, options);
}

export function validateMaasGroupedGpuUsageForSubmit(
  spec: Pick<MaasGroupedWorkloadSpec, 'headGroupSpec' | 'workerGroupSpecs' | 'gateway'>,
  configs: GpuResourceConfigurationItem[] | undefined,
  options?: GpuValidationOptions,
): MaasSubmitError | undefined {
  const violation = findGpuUsageViolationFromGroupedSpec(spec, configs, options);
  if (!violation) return undefined;
  return {
    key: MAAS_GPU_USAGE_EXCEEDS_MAX_KEY,
    params: {
      resourceType: violation.resourceType,
      vendor: violation.vendor,
      device: violation.device,
      total: violation.totalUsage,
      max: violation.maxQuantity,
    },
  };
}

/** 提交校验：实时拉取 resource-configurations 后再校验 GPU 总用量 */
export async function validateMaasGroupedGpuUsageLatest(
  spec: Pick<MaasGroupedWorkloadSpec, 'headGroupSpec' | 'workerGroupSpecs' | 'gateway'>,
  options?: GpuValidationOptions,
): Promise<MaasSubmitError | undefined> {
  const configs = await fetchContainerGpuResourceConfigurations();
  return validateMaasGroupedGpuUsageForSubmit(spec, configs, options);
}

/** 节点弹窗 / 表单中间校验：使用页面缓存的 resource-configurations 查找 GPU 用量违规 */
export async function findGpuUsageViolationLatest(
  nodes: MaasGroupSpec[],
  options?: GpuValidationOptions,
): Promise<GpuUsageViolation | undefined> {
  const configs = await getCachedContainerGpuResourceConfigurations();
  return findGpuUsageViolation(nodes, configs, options);
}

/** 表单字段中间校验：使用页面缓存的 resource-configurations 查找 GPU 用量违规 */
export async function findGpuUsageViolationFromNodeFieldsLatest(
  masterValue: unknown,
  workerValue: unknown,
  runMode: MaasRunMode | undefined,
  options?: GpuValidationOptions,
): Promise<GpuUsageViolation | undefined> {
  const nodes = collectMaasNodesForGpuValidation(parseNodesField(masterValue), parseNodesField(workerValue), runMode);
  return findGpuUsageViolationLatest(nodes, options);
}
